/**
 * Leituras da tela Sincronização: nome de cada tabela do ERP em português, que
 * tela ela alimenta, quando está atrasada e como explicar o erro do conector.
 * O payload vem de GET /sync/overview (apps/api/src/modules/sync/sync-overview.service.ts).
 */
import type { AttentionPoint } from "./intelligence/executive";
export type EntityState = "ok" | "failed" | "running" | "interrupted" | "never";

export interface SyncEntity {
  entity: string;
  source: "erp" | "finatto";
  state: EntityState;
  lastSuccessAt: string | null;
  lastFullAt: string | null;
  lastError: string | null;
  lastErrorAt: string | null;
  runs24h: number;
  failures24h: number;
  changed24h: number;
}

export interface SyncCycle {
  slot: string;
  startedAt: string;
  finishedAt: string | null;
  jobs: number;
  failed: number;
  failedEntities: string[];
  running: number;
  interrupted: number;
  inserted: number;
  updated: number;
  full: boolean;
  lastError: string | null;
}

export interface SyncOverview {
  generatedAt: string;
  entities: SyncEntity[];
  cycles: SyncCycle[];
  staleRunning: number;
  schedules: { incremental: string; nightlyFull: string; finatto: string | null };
}

export type Area = "Cadastros" | "Financeiro" | "Operação" | "Frota" | "Pneus (Finatto)";
export const AREAS: Area[] = ["Financeiro", "Operação", "Frota", "Cadastros", "Pneus (Finatto)"];

type Info = { label: string; area: Area; feeds: string };

const ENTITY_INFO: Record<string, Info> = {
  customers:           { label: "Clientes", area: "Cadastros", feeds: "Nomes e grupos de cliente em todas as telas" },
  suppliers:           { label: "Fornecedores", area: "Cadastros", feeds: "Contas a pagar e custos" },
  financial_accounts:  { label: "Plano de contas", area: "Cadastros", feeds: "DRE e classificação de custos" },
  cost_centers:        { label: "Centros de custo", area: "Cadastros", feeds: "DRE e custos" },
  bank_accounts:       { label: "Contas bancárias", area: "Cadastros", feeds: "Saldo e fluxo de caixa" },
  vehicles:            { label: "Veículos", area: "Cadastros", feeds: "Frota, placas e manutenção" },
  drivers:             { label: "Motoristas", area: "Cadastros", feeds: "Motoristas e acertos" },
  accounts_receivable: { label: "Contas a receber", area: "Financeiro", feeds: "Financeiro, inadimplência e fluxo de caixa" },
  receipts:            { label: "Recebimentos", area: "Financeiro", feeds: "Recebido no período e fluxo de caixa" },
  accounts_payable:    { label: "Contas a pagar", area: "Financeiro", feeds: "Financeiro e fluxo de caixa" },
  payments:            { label: "Pagamentos", area: "Financeiro", feeds: "Pago no período e fluxo de caixa" },
  bank_movements:      { label: "Movimentos bancários", area: "Financeiro", feeds: "Fluxo de caixa realizado" },
  cash_movements:      { label: "Movimentos de caixa", area: "Financeiro", feeds: "Fluxo de caixa realizado" },
  cashflow_projection: { label: "Projeção de caixa", area: "Financeiro", feeds: "Fluxo de caixa projetado" },
  boletos:             { label: "Boletos", area: "Financeiro", feeds: "Contas a receber" },
  purchase_invoices:   { label: "Notas de compra", area: "Financeiro", feeds: "Custos e DRE" },
  cte:                 { label: "CT-e", area: "Operação", feeds: "Faturamento, Operacional, Executiva e DRE" },
  freight_letters:     { label: "Cartas frete", area: "Operação", feeds: "Custo de terceiros e margem" },
  freight_settlements: { label: "Acertos de frete", area: "Operação", feeds: "Custo com motoristas" },
  trips:               { label: "Viagens", area: "Operação", feeds: "Motoristas, pedágio e seguro" },
  fuel:                { label: "Abastecimentos", area: "Frota", feeds: "Combustível, km/l e custo por placa" },
  tires:               { label: "Pneus (ERP)", area: "Frota", feeds: "Substituído pela Finatto" },
  maintenance:         { label: "Manutenções", area: "Frota", feeds: "Manutenção e custo por placa" },
  maintenance_plan:    { label: "Plano de manutenção", area: "Frota", feeds: "Preventivas" },
  vehicle_costs:       { label: "Custos por veículo", area: "Frota", feeds: "Custo e margem por placa" },
  finatto_tires_catalog:     { label: "Cadastro de pneus", area: "Pneus (Finatto)", feeds: "Pneus" },
  finatto_tire_movements:    { label: "Movimentações de pneus", area: "Pneus (Finatto)", feeds: "Pneus" },
  finatto_tire_costs:        { label: "Custos e recapagens", area: "Pneus (Finatto)", feeds: "Pneus e custo por placa" },
  finatto_tire_writeoffs:    { label: "Baixas de pneus", area: "Pneus (Finatto)", feeds: "Pneus" },
  finatto_vehicle_positions: { label: "Pneus montados por placa", area: "Pneus (Finatto)", feeds: "Pneus por veículo" },
  finatto_tires_full:        { label: "Carga completa de pneus", area: "Pneus (Finatto)", feeds: "Recarga manual" },
};

/** Entidades que o sistema não usa mais (a fonte mudou): não contam como problema. */
const RETIRED = new Set(["tires", "finatto_tires_full"]);

export function entityInfo(entity: string): Info {
  return ENTITY_INFO[entity] ?? { label: entity, area: entity.startsWith("finatto") ? "Pneus (Finatto)" : "Cadastros", feeds: "" };
}

/** Atraso tolerado: o ERP roda a cada 30 min (3 ciclos perdidos = atrasado); a Finatto, 1x por dia. */
const LATE_MS = { erp: 90 * 60_000, finatto: 26 * 3_600_000 };

export type Health = { tone: "ok" | "warn" | "bad" | "muted"; label: string };

export function entityHealth(e: SyncEntity, now: Date): Health {
  if (RETIRED.has(e.entity)) return { tone: "muted", label: e.entity === "tires" ? "Não usada" : "Sob demanda" };
  if (e.state === "never") return { tone: "muted", label: "Nunca rodou" };
  if (e.state === "running") return { tone: "ok", label: "Rodando agora" };
  if (e.state === "interrupted") return { tone: "bad", label: "Travada" };
  if (e.state === "failed") return { tone: "bad", label: "Falhou" };
  if (!e.lastSuccessAt || now.getTime() - new Date(e.lastSuccessAt).getTime() > LATE_MS[e.source]) return { tone: "warn", label: "Atrasada" };
  return { tone: "ok", label: "Em dia" };
}

/** Explica o erro do conector em uma frase; o texto original fica no title. */
export function explainError(msg: string | null): string | null {
  if (!msg) return null;
  const host = msg.match(/\((\d{1,3}(?:\.\d{1,3}){3})\)/)?.[1];
  if (/inacess[ií]vel|ETIMEDOUT|ECONNREFUSED|Connection terminated/i.test(msg)) return `ERP fora do ar ou fora da rede${host ? ` (servidor ${host} não respondeu)` : ""}`;
  if (/statement timeout|Query read timeout/i.test(msg)) return "A consulta demorou demais no ERP e foi cancelada";
  if (/shutting down|starting up/i.test(msg)) return "O banco do ERP estava reiniciando";
  if (/Job interrompido/i.test(msg)) return "Interrompido por reinício do BI";
  return msg;
}

export const isUnreachable = (msg: string | null) => !!msg && /inacess[ií]vel|ETIMEDOUT|ECONNREFUSED|Connection terminated/i.test(msg);

const DAY = 86_400_000;

/** "há 12 min", "há 3 h", "há 7 dias". */
export function ago(iso: string, now: Date): string {
  const ms = Math.max(0, now.getTime() - new Date(iso).getTime());
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "agora";
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (ms < DAY) return `há ${h} h`;
  const d = Math.floor(ms / DAY);
  return `há ${d} dia${d > 1 ? "s" : ""}`;
}

const tz = { timeZone: "America/Sao_Paulo" } as const;
/** "19/10 às 11:31" */
export function fmtWhen(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString("pt-BR", { ...tz, day: "2-digit", month: "2-digit" })} às ${d.toLocaleTimeString("pt-BR", { ...tz, hour: "2-digit", minute: "2-digit" })}`;
}

/** Próxima meia hora cheia (o incremental roda em :00 e :30). */
export function nextCycle(now: Date): Date {
  const d = new Date(now);
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() < 30 ? 30 : 60);
  return d;
}

/** "0 5 * * *" → "05:00"; outras formas voltam como estão. */
export function cronTime(expr: string): string {
  const m = expr.trim().match(/^(\d{1,2}) (\d{1,2}) \* \* \*$/);
  return m ? `${m[2]!.padStart(2, "0")}:${m[1]!.padStart(2, "0")}` : expr;
}

export type SyncSummary = {
  /** último sucesso mais recente do ERP */
  lastUpdate: string | null;
  /** tabelas acompanhadas (ERP + Finatto, sem as aposentadas) */
  tracked: number;
  late: SyncEntity[];
  broken: SyncEntity[];
  cycles24h: number;
  failedCycles24h: number;
  unreachable24h: number;
  status: { tone: "ok" | "warn" | "bad"; title: string; detail: string };
};

export function summarize(o: SyncOverview, now: Date): SyncSummary {
  const erp = o.entities.filter((e) => e.source === "erp" && !RETIRED.has(e.entity));
  const lastUpdate = erp.reduce<string | null>((max, e) => (e.lastSuccessAt && (!max || e.lastSuccessAt > max) ? e.lastSuccessAt : max), null);
  const tones = new Map(o.entities.map((e) => [e.entity, entityHealth(e, now).tone]));
  const late = o.entities.filter((e) => tones.get(e.entity) === "warn");
  const broken = o.entities.filter((e) => tones.get(e.entity) === "bad");
  const recent = o.cycles.filter((c) => now.getTime() - new Date(c.startedAt).getTime() < DAY);
  const failedCycles24h = recent.filter((c) => c.failed > 0).length;
  const unreachable24h = recent.filter((c) => isUnreachable(c.lastError)).length;

  const latest = o.cycles[0];
  let status: SyncSummary["status"];
  if (!lastUpdate) status = { tone: "bad", title: "O BI nunca recebeu dados do ERP", detail: "Confira a conexão em Configurações → Bancos de Dados." };
  else if (now.getTime() - new Date(lastUpdate).getTime() > LATE_MS.erp) {
    status = { tone: "bad", title: `Os dados do ERP pararam de chegar ${ago(lastUpdate, now)}`, detail: latest && isUnreachable(latest.lastError) ? "O último ciclo não alcançou o servidor do ERP." : `Última atualização em ${fmtWhen(lastUpdate)}. Os painéis mostram os números até esse horário.` };
  } else if (latest && latest.failed > 0) {
    status = { tone: "warn", title: "O último ciclo teve falhas", detail: `${explainError(latest.lastError) ?? "Erro no conector"}. Os dados chegaram ${ago(lastUpdate, now)}.` };
  } else status = { tone: "ok", title: "Dados em dia", detail: `Última atualização ${ago(lastUpdate, now)} (${fmtWhen(lastUpdate)}).` };

  const tracked = o.entities.filter((e) => !RETIRED.has(e.entity)).length;
  return { lastUpdate, tracked, late, broken, cycles24h: recent.length, failedCycles24h, unreachable24h, status };
}

/**
 * Fila "O que pede ação" da Sincronização, no formato da Visão Executiva.
 * Ordem: dados parados, tabelas quebradas, tabelas atrasadas, quedas do ERP no
 * dia e registros de execução órfãos (estes não afetam dado nenhum).
 */
export function syncAttention(o: SyncOverview, sum: SyncSummary, now: Date): AttentionPoint[] {
  const pts: Omit<AttentionPoint, "priority">[] = [];
  const row = (e: SyncEntity) => ({ label: entityInfo(e.entity).label, value: explainError(e.lastError) && e.state !== "ok" ? explainError(e.lastError)! : e.lastSuccessAt ? `último sucesso ${ago(e.lastSuccessAt, now)}` : "nunca rodou" });

  if (sum.status.tone === "bad" && sum.lastUpdate) pts.push({
    id: "dados-parados", severity: "critical", memory: null,
    title: "Os dados do ERP pararam de chegar",
    fact: `A última atualização foi ${ago(sum.lastUpdate, now)}, em ${fmtWhen(sum.lastUpdate)}. Desde então os painéis mostram os números desse horário.`,
    impact: `Painéis parados em ${fmtWhen(sum.lastUpdate)}`,
    action: "Confira se o servidor do ERP está ligado e acessível pela rede do BI; depois use Sincronizar agora.",
    evidence: o.cycles.slice(0, 3).map((c) => ({ label: `Ciclo de ${fmtWhen(c.startedAt)}`, value: c.failed ? `${c.failed} de ${c.jobs} com falha` : "sem falha" })),
  });
  if (sum.broken.length) pts.push({
    id: "tabelas-com-falha", severity: "warning", memory: null,
    title: sum.broken.length > 1 ? "Tabelas com falha ou travadas" : "Tabela com falha ou travada",
    fact: `${sum.broken.map((e) => entityInfo(e.entity).label).join(", ")} ${sum.broken.length > 1 ? "não terminaram" : "não terminou"} a última execução.`,
    impact: `${sum.broken.length} tabela${sum.broken.length > 1 ? "s" : ""} sem a última atualização`,
    action: "Se o erro for do ERP, o próximo ciclo costuma resolver sozinho. Se repetir, abra a aba Tabelas e veja o erro.",
    evidence: sum.broken.map(row),
  });
  if (sum.late.length && sum.status.tone !== "bad") pts.push({
    id: "tabelas-atrasadas", severity: "warning", memory: null,
    title: "Tabelas atrasadas",
    fact: `${sum.late.map((e) => entityInfo(e.entity).label).join(", ")} não ${sum.late.length > 1 ? "são atualizadas" : "é atualizada"} há mais tempo que o normal.`,
    impact: `${sum.late.length} tabela${sum.late.length > 1 ? "s" : ""} desatualizada${sum.late.length > 1 ? "s" : ""}`,
    action: "Veja na aba Tabelas desde quando cada uma está parada.",
    evidence: sum.late.map(row),
  });
  if (sum.unreachable24h) pts.push({
    id: "erp-fora", severity: "warning", memory: null,
    title: "ERP fora do ar nas últimas 24 horas",
    fact: `Em ${sum.unreachable24h} de ${sum.cycles24h} ciclos o BI não alcançou o servidor do ERP.`,
    impact: `${sum.unreachable24h} ciclo${sum.unreachable24h > 1 ? "s" : ""} sem atualização`,
    action: "Quedas curtas se resolvem no ciclo seguinte. Se forem frequentes, verifique a conexão entre o BI e o servidor do ERP.",
    evidence: o.cycles.filter((c) => isUnreachable(c.lastError)).slice(0, 5).map((c) => ({ label: fmtWhen(c.startedAt), value: explainError(c.lastError) ?? "" })),
  });
  if (o.staleRunning) pts.push({
    id: "registros-travados", severity: "info", memory: null,
    title: "Execuções que ficaram marcadas como rodando",
    fact: `${o.staleRunning.toLocaleString("pt-BR")} ${o.staleRunning > 1 ? "execuções ficaram abertas" : "execução ficou aberta"} depois de reinícios do BI. Os dados não são afetados: o ciclo seguinte refaz o trabalho.`,
    impact: "Só o histórico",
    action: "Nenhuma ação nos dados. O suporte técnico pode fechar esses registros.",
    evidence: [{ label: "Abertas há mais de 2 horas", value: o.staleRunning.toLocaleString("pt-BR") }],
  });
  return pts.map((p, i) => ({ ...p, priority: i + 1 }));
}
